// Caisse.jsx - Modified version

import { Box, Drawer, Tab, Tabs, Modal, Typography, TextField, Button } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import CashPayment from "../Components/Caisse/payment/cash";
import CreditCardPayment from "../Components/Caisse/payment/creditcard";
import ChequePayment from "../Components/Caisse/payment/cheque";
import GiftCardPayment from "../Components/Caisse/payment/giftcard";
import { getCategories } from "../Services/CategrieApi";
import { useUser } from "../Auth/useUser";
import { useOrganisations } from "../Auth/useOrganisations";
import { useCaisse } from "../Auth/useCaisse";
import { useActiveJournal } from "../Auth/useActiveJournal";
import Product from "../Components/Caisse/Product";
import ProductSimplified from "../Components/Caisse/ProductSimplified";
import TablesDisplay from "../Components/Caisse/Tables";
import { getAllTables } from "../Services/TablesApi";
import { getActiveSession, getTableSessionByID } from "../Services/CartApi";
import { toast } from "react-toastify";
import GlobalMenu from "../Components/Caisse/MenuCaisse/menu";
import CaisseCart from "../Components/Caisse/Caisse_Cart";
import CaisseOrders from "../Components/Caisse/Caisse_orders";
import Calculator from "../Components/Caisse/keyboards/Calculator";
import KeyboardComponent from "../Components/Caisse/keyboards/virtualKeypad";
import { convertCartsToCartItems } from "../Helpers/Helpers";
import { getParametrageCaisse } from "../Services/ParametrageApi";
import { updateProductQuickFields } from "../Services/MenuApi";
import TablesModal from "../Components/Caisse/TablesModal";
import ReloginModal from "../Components/Caisse/UnlockCaissePopUp";
import { useToken, performLogout } from "../Auth/useToken";
import { getDefaultClientByOrganisation } from "../Services/ClientApi";
import { getCaisseFromStorage } from "../Services/CaisseApi";
import {
  getActiveCashJournal,
  createCashJournal,
  updateCashJournal,
} from "../Services/JournalApi";

// localStorage key
const CART_STORAGE_KEY = "pos_cart_items";
const SELECTED_TABLE_KEY = "pos_selected_table";

const Caisse = () => {
  const navigate = useNavigate();
  const [categories, setcategories] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [orderItems, setOrdersItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [parametrage, setParametrage] = useState();
  const [selectedTable, setSelectedTable] = useState(null);
  const [occupiedTableData, setOccupiedTableData] = useState(null);
  const [refresh, setRefresh] = useState(true);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const isLocked = JSON.parse(localStorage.getItem("locked"));
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [
    organisations,
    setOrganisations,
    usedOrganisations,
    setUsedOrganisations,
  ] = useOrganisations();

  // ── Admin-as-cashier onboarding ──────────────────────────────────────
  // An admin doesn't come through the caissier login flow (which auto-opens
  // a journal for them), so when an admin lands on /caisse we set one up
  // here on demand, gated behind a "fond initial" prompt exactly like the
  // one caissiers see at login.
  const user = useUser();
  const isAdmin = user?.result?.role === "admin";
  const { initCaisse } = useCaisse();
  const [, setActiveJournal] = useActiveJournal();
  const [sessionReady, setSessionReady] = useState(!isAdmin);
  const [showFundModal, setShowFundModal] = useState(false);
  const [fondInitial, setFondInitial] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingJournal, setPendingJournal] = useState(null);
  const [pendingCaisseId, setPendingCaisseId] = useState(null);
  const setupStartedRef = useRef(false);

  useEffect(() => {
    if (!isAdmin || !usedOrganisations || sessionReady) return;
    // StrictMode (and any effect re-entrancy) double-invokes this in dev;
    // without a guard, two concurrent get-or-create calls can race and
    // trip the caisse code's unique index.
    if (setupStartedRef.current) return;
    setupStartedRef.current = true;

    const setupAdminCaisseSession = async () => {
      try {
        const storedCaisse = getCaisseFromStorage();
        const storedJournal = JSON.parse(
          localStorage.getItem("activeJournal") || "null",
        );

        if (
          storedCaisse?.user_id === user.result.id &&
          storedJournal &&
          !storedJournal.cloture &&
          storedJournal.fond_initial > 0
        ) {
          setSessionReady(true);
          return;
        }

        const caisseData = await initCaisse(
          user.result,
          usedOrganisations,
          `Caisse_${user.result.prenom}_${user.result.nom}`,
        );

        if (!caisseData) {
          toast.error("Erreur lors de l'initialisation de la caisse");
          return;
        }

        const caisseId = caisseData.id || caisseData._id;
        // A 404 here just means this organisation has no active journal yet
        // (e.g. its very first caisse session) — that's expected, not fatal.
        let journalRes = null;
        try {
          journalRes = await getActiveCashJournal(usedOrganisations);
        } catch (journalError) {
          if (journalError.response?.status !== 404) throw journalError;
        }
        const foundJournal =
          journalRes?.data?.success !== "0" ? journalRes?.data?.data : null;
        const ownJournal =
          foundJournal &&
          (foundJournal.caisse_id === caisseId ||
            foundJournal.caisse_id === caisseData._id)
            ? foundJournal
            : null;

        if (ownJournal && ownJournal.fond_initial > 0) {
          setActiveJournal(ownJournal);
          setSessionReady(true);
        } else {
          setPendingJournal(ownJournal);
          setPendingCaisseId(caisseId);
          setShowFundModal(true);
        }
      } catch (error) {
        console.error("Error setting up admin caisse session:", error);
        toast.error("Erreur lors de l'initialisation de la caisse");
      }
    };

    setupAdminCaisseSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, usedOrganisations, sessionReady]);

  const handleFundModalSubmit = async () => {
    const fundValue = parseFloat(fondInitial);
    if (isNaN(fundValue) || fundValue < 0) {
      toast.error("Veuillez entrer un montant valide");
      return;
    }

    setIsProcessing(true);
    try {
      let journal;
      if (pendingJournal) {
        const res = await updateCashJournal(
          pendingJournal._id || pendingJournal.id,
          {
            fond_initial: fundValue,
            fond_courant: fundValue,
            caisse_id: pendingCaisseId,
          },
        );
        journal = res.data.data;
      } else {
        const createRes = await createCashJournal(
          { fond_initial: fundValue, user_id: user.result.id },
          usedOrganisations,
        );
        const created = createRes.data.data;
        const linkRes = await updateCashJournal(created._id || created.id, {
          caisse_id: pendingCaisseId,
          fond_courant: fundValue,
        });
        journal = linkRes.data.data;
      }

      setActiveJournal(journal);
      setShowFundModal(false);
      setFondInitial("");
      setPendingJournal(null);
      setSessionReady(true);
      toast.success(`Fond initial défini à ${fundValue.toFixed(3)} TND`);
    } catch (error) {
      console.error("Error configuring cash journal:", error);
      toast.error("Erreur lors de la configuration du journal de caisse");
    } finally {
      setIsProcessing(false);
    }
  };

  // Fund modal keyboard handler (numeric context on Electron)
  const handleFundKeyPress = (key) => {
    if (isProcessing) return;
    if (key === "←") {
      setFondInitial((v) => v.slice(0, -1));
    } else if (key === "prix" || key === "✔") {
      handleFundModalSubmit();
    } else if (key === ".") {
      if (!fondInitial.includes(".")) setFondInitial((v) => v + ".");
    } else if (/^\d$/.test(key)) {
      setFondInitial((v) => v + key);
    }
    // ignore %, -
  };

  // Load default client
  useEffect(() => {
    if (usedOrganisations) {
      loadDefaultClient();
    }
  }, [usedOrganisations]);

  const loadDefaultClient = async () => {
    try {
      const response = await getDefaultClientByOrganisation(usedOrganisations);
      if (response.data.success && response.data.data) {
        setSelectedClient(response.data.data);
      }
    } catch (error) {
      console.error("Error loading default client:", error);
    }
  };

  const handleOccupiedTableClick = async (tableData) => {
    try {
      setSelectedTable(tableData);
      const response = await getActiveSession(tableData._id);

      if (response.data.success !== "0") {
        const { session, carts, tickets, remainingCarts, transactions } =
          response.data.data;
        setTransactions(transactions);
        if (session && carts) {
          setOccupiedTableData({
            table: tableData,
            session: session,
            carts: carts,
          });
          const cartItemsFromSession = convertCartsToCartItems(
            remainingCarts,
            parametrage?.products_grouping,
          );
          setOrdersItems(cartItemsFromSession);
        } else {
          console.log("No active session found for this table");
          toast.info("Aucune session active pour cette table");
          setOccupiedTableData(null);
          setCartItems([]);
        }
      } else {
        console.error("Failed to fetch session data");
        toast.error("Erreur lors du chargement de la session");
      }
    } catch (error) {
      console.error("Error in handleOccupiedTableClick:", error);
      toast.error("Erreur lors du chargement de la table occupée");
    }
  };

  const handleSelectTableSession = async (sessionData) => {
    console.log("sessionData", sessionData);
    try {
      setSelectedTable(sessionData.table_id);
      const response = await getTableSessionByID(sessionData._id);
      console.log(response);
      if (response.data.success !== "0") {
        const { session: sessionDetails, carts, payments } = response.data.data;
        if (payments) {
          setPayments(payments);
        }

        if (sessionDetails && carts) {
          setOccupiedTableData({
            table: sessionData.table_id,
            session: sessionDetails,
            carts: carts,
          });
          const cartItemsFromSession = convertCartsToCartItems(
            carts,
            parametrage?.products_grouping,
          );
          setOrdersItems(cartItemsFromSession);
          console.log("Session loaded successfully:", sessionDetails._id);
        } else {
          console.log("No session found for this id");
          toast.info("Aucune session trouvée");
          setOccupiedTableData(null);
          setOrdersItems([]);
        }
      } else {
        console.error("Failed to fetch session data:", response.data);
        toast.error("Erreur lors du chargement de la session");
      }
    } catch (error) {
      console.error("Error in handleSelectTableSession:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      toast.error(
        "Erreur lors du chargement de la session: " +
          (error.message || "Unknown error"),
      );
    }
  };

  useEffect(() => {
    if (selectedTable && occupiedTableData) {
      const isSameTable = occupiedTableData.table._id === selectedTable._id;
      if (!isSameTable) {
        setOccupiedTableData(null);
        setCartItems([]);
      }
    }
  }, [selectedTable, occupiedTableData]);

  const { t } = useTranslation();

  // Load cart and selected table from localStorage on component mount
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    const savedTable = localStorage.getItem(SELECTED_TABLE_KEY);

    if (savedCart) {
      console.log("savedCart", JSON.parse(savedCart));
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error("Error loading cart from localStorage:", error);
        setCartItems([]);
      }
    }

    if (savedTable) {
      try {
        setSelectedTable(JSON.parse(savedTable));
      } catch (error) {
        console.error("Error loading table from localStorage:", error);
        setSelectedTable(null);
      }
    }
  }, []);

  // Save cart to localStorage whenever cartItems changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  // Save selected table to localStorage whenever it changes
  useEffect(() => {
    if (selectedTable) {
      localStorage.setItem(SELECTED_TABLE_KEY, JSON.stringify(selectedTable));
    } else {
      localStorage.removeItem(SELECTED_TABLE_KEY);
    }
  }, [selectedTable]);

  useEffect(() => {
    if (parametrage?.chargementProduits !== false) {
      getCategories(usedOrganisations).then((res) => {
        const success = res.data.success;
        if (success === "0") {
          alert("error");
        } else {
          setcategories(res.data.data);
        }
      });
    }
  }, [usedOrganisations, refresh, parametrage?.chargementProduits]);

  useEffect(() => {
    getParametrageCaisse(usedOrganisations).then((res) => {
      const success = res.data.success;
      if (success === "0") {
        alert("error");
      } else {
        setParametrage(res.data.data);
      }
    });
  }, [usedOrganisations]);

  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);

  const loadTables = async () => {
    try {
      setLoading(true);
      const response = await getAllTables(usedOrganisations);
      if (response.data.success !== "0") {
        const tablesData = response.data.data;
        setTables(tablesData);

        if (handleTablesLoaded) {
          handleTablesLoaded(tablesData);
        }

        if (!selectedTable && tablesData && tablesData.length > 0) {
          const defaultTable = tablesData.find(
            (table) => table.default === true,
          );
          if (defaultTable && handleTableSelect) {
            handleTableSelect(defaultTable);
          }
        }
      }
    } catch (error) {
      console.error("Error loading tables:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal) loadTables();
  }, [usedOrganisations, refresh, showModal]);

  const getIngredientsTotal = (ingredients = []) =>
    ingredients.reduce((sum, ing) => sum + (ing.prix || 0), 0);

  // Add product to cart
  const addToCart = (product, discount = 0) => {
    console.log("product", product);

    if (!occupiedTableData || occupiedTableData.session.status !== "inactive") {
      setCartItems((prevItems) => {
        const existingItem = prevItems.find((item) => item.id === product.id);
        const ingredientsTotal = getIngredientsTotal(product.ingredients);
        const unitPrice = product.price + ingredientsTotal;

        // The product's standing (catalog) discount, if any, pre-fills the
        // cart line; the cashier can still override it for this order only.
        const remise = product.productInfo?.remise || discount || 0;

        if (existingItem) {
          if (product.productInfo.unite === "pcs") {
            // Determines how much to add (defaults to 1 if not specified)
            const quantityToAdd = product.productInfo.prix_quantite || 1;
            const newQuantity = existingItem.quantity + quantityToAdd;

            const totalPrice = unitPrice * newQuantity;
            const netPrice = totalPrice - (totalPrice * remise) / 100;

            return prevItems.map((item) =>
              item.id === product.id
                ? {
                    ...item,
                    quantity: newQuantity,
                    totalPrice,
                    remise,
                    netPrice,
                  }
                : item,
            );
          } else {
            // Currently, adding an existing non-pcs item does nothing.
            // You may want to add logic here to aggregate weights/volumes.
            return prevItems;
          }
        } else {
          // New item logic
          const initialQuantity = product.productInfo.prix_quantite || 1;
          console.log(unitPrice, initialQuantity);
          const totalPrice =
            (unitPrice / product.productInfo.prix_quantite) * initialQuantity; // Fixed: now respects actual quantity
          const netPrice = totalPrice - (totalPrice * remise) / 100;

          const newCartItem = {
            id: product.id,
            quantity: initialQuantity,
            price: product.price,
            ingredientsPrice: ingredientsTotal,
            totalPrice,
            remise,
            netPrice,
            productInfo: product.productInfo,
            ingredients: product.ingredients,
          };

          return [...prevItems, newCartItem];
        }
      });
    }
  };

  // Remove product from cart
  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId),
    );
  };

  // Update product quantity
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    console.log("quantity", quantity);
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId
          ? {
              ...item,
              quantity: quantity,
              totalPrice: item.price * quantity,
              netPrice: item.price * quantity,
            }
          : item,
      ),
    );
  };

  // ── Focused single-item editor + Calculator ──────────────────────────
  // Long-pressing a cart row (in CaisseCart) opens a focused view for that
  // item; tapping its price/remise/quantity binds the shared Calculator
  // (rendered here) to that field. Lives at this level so the same
  // Calculator can eventually be reused by sibling search inputs too.
  const [focusedItemId, setFocusedItemId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [fieldBuffer, setFieldBuffer] = useState("");
  const [calculatorShowLetters, setCalculatorShowLetters] = useState(false);

  // Which OTHER (non-cart-field) input the Calculator is bound to —
  // ClientSelector's search box, or the Product search/barcode input.
  // Only one of editingField / calcTarget is ever active at a time.
  const [calcTarget, setCalcTarget] = useState(null); // null | "clientSearch" | "productSearch"
  const [calcBuffer, setCalcBuffer] = useState("");

  const activateClientSearchCalc = (initialValue) => {
    setEditingField(null);
    setFieldBuffer("");
    setCalcTarget("clientSearch");
    setCalcBuffer(initialValue || "");
  };

  const activateProductSearchCalc = (initialValue) => {
    setEditingField(null);
    setFieldBuffer("");
    setCalcTarget("productSearch");
    setCalcBuffer(initialValue || "");
  };

  const deactivateSearchCalc = () => {
    setCalcTarget(null);
    setCalcBuffer("");
  };

  const updateItemPrice = (itemId, newPrice) => {
    setCartItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const totalPrice = newPrice * item.quantity;
        const remisePercent = item.remise || 0;
        const remiseAmount = (totalPrice * remisePercent) / 100;
        return {
          ...item,
          price: newPrice,
          totalPrice,
          netPrice: totalPrice - remiseAmount,
        };
      }),
    );
  };

  const updateItemRemise = (itemId, remise) => {
    setCartItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const discountedPrice = item.price - (item.price * remise) / 100;
        return {
          ...item,
          remise,
          netPrice: discountedPrice * item.quantity,
        };
      }),
    );
  };

  const handleWeightQuantityChange = (item, newWeight) => {
    const weight = parseFloat(newWeight) || 0;
    const prixQuantite = item.productInfo?.prix_quantite || 1;
    const prixTtc = item.productInfo?.prix_ttc || item.price;
    const calculatedPrice = (weight / prixQuantite) * prixTtc;

    setCartItems((prev) =>
      prev.map((cartItem) => {
        if (cartItem.id !== item.id) return cartItem;
        const remisePercent = cartItem.remise || 0;
        const remiseAmount = (calculatedPrice * remisePercent) / 100;
        return {
          ...cartItem,
          quantity: weight,
          totalPrice: calculatedPrice,
          netPrice: calculatedPrice - remiseAmount,
        };
      }),
    );
  };

  // Close the focused item view if it leaves the cart (e.g. removed elsewhere)
  useEffect(() => {
    if (
      focusedItemId &&
      ![...cartItems, ...orderItems].some((i) => i.id === focusedItemId)
    ) {
      setFocusedItemId(null);
      setEditingField(null);
      setFieldBuffer("");
    }
  }, [cartItems, orderItems, focusedItemId]);

  const focusedItem = focusedItemId
    ? [...cartItems, ...orderItems].find((i) => i.id === focusedItemId)
    : null;

  // Apply the on-screen calculator's typed buffer to the focused item's field
  const applyFieldBuffer = (field, bufferStr) => {
    if (!focusedItem) return;
    if (field === "quantity") {
      const effective = bufferStr || "1";
      const isWeight =
        focusedItem.productInfo?.unite &&
        focusedItem.productInfo.unite !== "pcs";
      if (isWeight) {
        handleWeightQuantityChange(focusedItem, effective);
      } else {
        const parsed = parseInt(effective, 10);
        updateQuantity(
          focusedItem.id,
          Number.isNaN(parsed) || parsed < 1 ? 1 : parsed,
        );
      }
      return;
    }
    if (field === "price") {
      const parsed = parseFloat(bufferStr || "0");
      updateItemPrice(focusedItem.id, Number.isNaN(parsed) ? 0 : parsed);
      return;
    }
    if (field === "remise") {
      const parsed = parseFloat(bufferStr || "0");
      const clamped = Number.isNaN(parsed)
        ? 0
        : Math.min(100, Math.max(0, parsed));
      updateItemRemise(focusedItem.id, clamped);
    }
  };

  // Push the calculator-typed buffer to the focused item/field whenever it settles
  useEffect(() => {
    if (editingField) {
      applyFieldBuffer(editingField, fieldBuffer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldBuffer, editingField]);

  const startEditingField = (field) => {
    setCalcTarget(null);
    setCalcBuffer("");
    setEditingField(field);
    setFieldBuffer("");
  };

  const stopEditingField = () => {
    setEditingField(null);
    setFieldBuffer("");
  };

  const openFocusedItem = (itemId) => {
    setCalcTarget(null);
    setCalcBuffer("");
    setFocusedItemId(itemId);
    setEditingField(null);
    setFieldBuffer("");
  };

  // Leaving the focused view is when we ask whether any price/remise edits
  // made in this session should stick to the product's catalog entry, or
  // stay a one-off override on this order's cart line (the default, already
  // applied locally as the cashier typed).
  const closeFocusedItem = () => {
    const item = focusedItem;
    setFocusedItemId(null);
    setEditingField(null);
    setFieldBuffer("");

    if (!item) return;

    const catalogPrice = item.productInfo?.prix_ttc;
    const catalogRemise = item.productInfo?.remise || 0;
    const priceChanged =
      catalogPrice !== undefined && item.price !== catalogPrice;
    const remiseChanged = (item.remise || 0) !== catalogRemise;

    if (!priceChanged && !remiseChanged) return;

    const changesList = [
      priceChanged ? `Prix : <b>${item.price.toFixed(3)} TND</b>` : null,
      remiseChanged ? `Remise : <b>${item.remise || 0}%</b>` : null,
    ]
      .filter(Boolean)
      .join("<br/>");

    Swal.fire({
      title: "Modifications du produit",
      html: `${changesList}<br/><br/>Ces changements doivent-ils être permanents (mise à jour du produit), ou ne s'appliquer qu'à cette commande ?`,
      icon: "question",
      showDenyButton: true,
      confirmButtonText: "Permanentes (produit)",
      denyButtonText: "Cette commande seulement",
      confirmButtonColor: "#28a745",
      denyButtonColor: "#6c757d",
    }).then((result) => {
      if (!result.isConfirmed) return; // "cette commande seulement" or dismissed — already applied locally

      updateProductQuickFields({
        id: item.productInfo?._id,
        prix_ttc: priceChanged ? item.price : undefined,
        remise: remiseChanged ? item.remise : undefined,
      })
        .then(() => {
          toast.success("Produit mis à jour définitivement.");
          setRefresh(!refresh);
        })
        .catch(() => {
          toast.error("Erreur lors de la mise à jour permanente du produit.");
        });
    });
  };

  const calculatorSlot = (
    <Calculator
      onKeyPress={(key) => {
        if (calcTarget === "clientSearch" || calcTarget === "productSearch") {
          if (key === "C") {
            setCalcBuffer("");
            return;
          }
          setCalcBuffer((prev) =>
            key === "←" ? prev.slice(0, -1) : prev + key,
          );
          return;
        }
        if (!editingField) return;
        if (key === "C") {
          setFieldBuffer("");
          return;
        }
        setFieldBuffer((prev) =>
          key === "←" ? prev.slice(0, -1) : prev + key,
        );
      }}
      showLetters={calculatorShowLetters}
      setShowLetters={setCalculatorShowLetters}
      context="numeric"
    />
  );

  // Clear entire cart
  const clearCart = () => {
    setSelectedTable(null);
    setCartItems([]);
    setOrdersItems([]);
    setPayments([]);
    localStorage.removeItem(CART_STORAGE_KEY);
    setOccupiedTableData(null);
  };

  // Calculate total using net prices
  const cartTotal = cartItems.reduce((total, item) => total + item.netPrice, 0);
  const orderTotal = orderItems.reduce(
    (total, item) => total + item.netPrice,
    0,
  );
  const total = cartTotal + orderTotal;

  // Get total items count
  const getCartItemsCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handleTableSelect = (table) => {
    clearCart();
    setSelectedTable(table);
  };

  // Function to set default table when tables are loaded
  const handleTablesLoaded = (tables) => {
    if (!selectedTable && tables && tables.length > 0) {
      const defaultTable = tables.find((table) => table.default === true);
      if (defaultTable) {
        setSelectedTable(defaultTable);
      }
    }
  };

  // Handle logout
  const handleLogout = () => {
    performLogout({ preserveKeys: ["caisse_data"] });
    navigate("/login", { replace: true });
  };

  // Handle orders button click
  const handleOrdersClick = () => {
    setOrdersModalOpen(true);
  };
  const handleTablesClick = () => {
    setShowModal(true);
  };
  // Handle orders modal close
  const handleOrdersModalClose = () => {
    setOrdersModalOpen(false);
  };
  const [lockShowModal, setLockShowModal] = useState(false);
  useEffect(() => {
    if (!selectedTable && !isLocked) {
      setShowModal(true);
    }
  }, [selectedTable, isLocked]);
  useEffect(() => {
    if (isLocked) {
      setLockShowModal(true);
    }
  }, [isLocked]);

  const [setToken] = useToken();

  const lockCaisse = async () => {
    localStorage.setItem("locked", "true");
    setLockShowModal(true);
  };

  // Determine which product component to render
  const shouldLoadProducts = parametrage?.chargementProduits !== false;

  const fundModal = (
    <Modal
      open={showFundModal}
      onClose={() => {}}
      disableEscapeKeyDown
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: 520,
          bgcolor: "#20304c",
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          gutterBottom
          sx={{ color: "#ebdeff", fontWeight: 700 }}
        >
          Configuration du Journal de Caisse
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "rgba(235,222,255,0.5)" }}
        >
          Définissez le fond initial de départ pour utiliser la caisse.
        </Typography>

        <TextField
          fullWidth
          label="Fond Initial (TND)"
          type="text"
          value={fondInitial}
          placeholder="0.000"
          inputProps={{
            readOnly: true,
            inputMode: "none",
            step: "0.001",
            min: "0",
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              color: "#ebdeff",
              "& fieldset": { borderColor: "rgba(235,222,255,0.15)" },
              "&:hover fieldset": { borderColor: "rgba(253,104,62,0.4)" },
              "&.Mui-focused fieldset": { borderColor: "#fd683e" },
            },
            "& .MuiInputLabel-root": { color: "rgba(235,222,255,0.45)" },
            "& .MuiInputLabel-root.Mui-focused": { color: "#fd683e" },
            "& input": { cursor: "pointer" },
          }}
          disabled={isProcessing}
        />

        <Box sx={{ mb: 2 }}>
          <KeyboardComponent
            context="numeric"
            onKeyPress={handleFundKeyPress}
            showLetters={false}
            setShowLetters={() => {}}
            disabled={isProcessing}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            onClick={() => navigate("/")}
            disabled={isProcessing}
            sx={{ color: "rgba(235,222,255,0.5)" }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleFundModalSubmit}
            variant="contained"
            disabled={isProcessing || !fondInitial}
            sx={{
              background: "linear-gradient(135deg, #fd683e, #6d28d9)",
              "&:hover": { opacity: 0.88 },
              "&.Mui-disabled": { opacity: 0.4, color: "#fff" },
            }}
          >
            {isProcessing ? "Traitement..." : "Confirmer"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );

  if (isAdmin && !sessionReady) {
    return (
      <div
        className="hold-transition light-skin sidebar-mini theme-danger fixed"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#888",
        }}
      >
        {fundModal}
        {!showFundModal && "Initialisation de la caisse..."}
      </div>
    );
  }

  return (
    <div className="hold-transition light-skin sidebar-mini theme-danger fixed">
      <div className="wrapper" style={{ paddingBottom: "90px" }}>
        <div className="row">
          {parametrage && parametrage.TableRequired ? (
            <div
              className="col-7"
              style={{
                height: "calc(100vh - 90px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>
                {shouldLoadProducts ? (
                  <Product
                    categories={categories}
                    onAddToCart={addToCart}
                    organisationId={usedOrganisations}
                    selectedClient={selectedClient}
                    onClientSelect={setSelectedClient}
                    onProductCreated={() => setRefresh(!refresh)}
                    clientSearchCalcActive={calcTarget === "clientSearch"}
                    clientSearchCalcBuffer={calcBuffer}
                    onActivateClientSearchCalc={activateClientSearchCalc}
                    onDeactivateClientSearchCalc={deactivateSearchCalc}
                    productSearchCalcActive={calcTarget === "productSearch"}
                    productSearchCalcBuffer={calcBuffer}
                    onActivateProductSearchCalc={activateProductSearchCalc}
                    onDeactivateProductSearchCalc={deactivateSearchCalc}
                  />
                ) : (
                  <ProductSimplified
                    onAddToCart={addToCart}
                    organisationId={usedOrganisations}
                    selectedClient={selectedClient}
                    onClientSelect={setSelectedClient}
                    chargementProduits={false}
                    onProductCreated={() => setRefresh(!refresh)}
                    clientSearchCalcActive={calcTarget === "clientSearch"}
                    clientSearchCalcBuffer={calcBuffer}
                    onActivateClientSearchCalc={activateClientSearchCalc}
                    onDeactivateClientSearchCalc={deactivateSearchCalc}
                    productSearchCalcActive={calcTarget === "productSearch"}
                    productSearchCalcBuffer={calcBuffer}
                    onActivateProductSearchCalc={activateProductSearchCalc}
                    onDeactivateProductSearchCalc={deactivateSearchCalc}
                  />
                )}
              </div>
              <TablesModal
                tables={tables}
                onTableSelect={handleTableSelect}
                selectedTable={selectedTable}
                onOccupiedTableClick={handleOccupiedTableClick}
                showModal={showModal}
                setShowModal={setShowModal}
              />
            </div>
          ) : (
            <div
              className="col-7"
              style={{
                height: "calc(100vh - 90px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>
                {shouldLoadProducts ? (
                  <Product
                    categories={categories}
                    onAddToCart={addToCart}
                    organisationId={usedOrganisations}
                    selectedClient={selectedClient}
                    onClientSelect={setSelectedClient}
                    onProductCreated={() => setRefresh(!refresh)}
                    clientSearchCalcActive={calcTarget === "clientSearch"}
                    clientSearchCalcBuffer={calcBuffer}
                    onActivateClientSearchCalc={activateClientSearchCalc}
                    onDeactivateClientSearchCalc={deactivateSearchCalc}
                    productSearchCalcActive={calcTarget === "productSearch"}
                    productSearchCalcBuffer={calcBuffer}
                    onActivateProductSearchCalc={activateProductSearchCalc}
                    onDeactivateProductSearchCalc={deactivateSearchCalc}
                  />
                ) : (
                  <ProductSimplified
                    onAddToCart={addToCart}
                    organisationId={usedOrganisations}
                    selectedClient={selectedClient}
                    onClientSelect={setSelectedClient}
                    chargementProduits={false}
                    onProductCreated={() => setRefresh(!refresh)}
                    clientSearchCalcActive={calcTarget === "clientSearch"}
                    clientSearchCalcBuffer={calcBuffer}
                    onActivateClientSearchCalc={activateClientSearchCalc}
                    onDeactivateClientSearchCalc={deactivateSearchCalc}
                    productSearchCalcActive={calcTarget === "productSearch"}
                    productSearchCalcBuffer={calcBuffer}
                    onActivateProductSearchCalc={activateProductSearchCalc}
                    onDeactivateProductSearchCalc={deactivateSearchCalc}
                  />
                )}
              </div>
              <div
                style={{
                  flex: "1 1 0%",
                  overflow: "hidden",
                }}
              >
                <TablesDisplay
                  tables={tables}
                  onTableSelect={handleTableSelect}
                  selectedTable={selectedTable}
                  onOccupiedTableClick={handleOccupiedTableClick}
                />
              </div>
            </div>
          )}

          <div
            className="col-5 pl-0"
            style={{
              height: "calc(100vh - 90px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CaisseCart
              parametrages={parametrage}
              cartItems={cartItems}
              setCartItems={setCartItems}
              orderItems={orderItems}
              payments={payments}
              previousPayments={transactions}
              onRemoveFromCart={removeFromCart}
              onUpdateQuantity={updateQuantity}
              onClearCart={clearCart}
              cartTotal={cartTotal}
              total={total}
              getCartItemsCount={getCartItemsCount}
              organisation={usedOrganisations}
              selectedTable={selectedTable}
              onTableSelect={handleTableSelect}
              refresh={refresh}
              setRefresh={setRefresh}
              occupiedTableData={occupiedTableData}
              lockCaisse={lockCaisse}
              onAddToCart={addToCart}
              selectedClient={selectedClient}
              onClientSelect={setSelectedClient}
              resetClientToDefault={loadDefaultClient}
              focusedItemId={focusedItemId}
              focusedItem={focusedItem}
              editingField={editingField}
              fieldBuffer={fieldBuffer}
              openFocusedItem={openFocusedItem}
              closeFocusedItem={closeFocusedItem}
              startEditingField={startEditingField}
              stopEditingField={stopEditingField}
              calculatorSlot={calculatorSlot}
            />
          </div>
        </div>
        {/* Global Menu at the bottom */}
        <GlobalMenu
          onOrdersClick={handleOrdersClick}
          onTablesClick={handleTablesClick}
          onLogoutClick={handleLogout}
          parametrages={parametrage}
        />
        {/* Orders Modal */}
        <Modal
          open={ordersModalOpen}
          onClose={handleOrdersModalClose}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "90%",
              height: "90%",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <h2 style={{ margin: 0 }}>Commandes</h2>
              <button
                className="btn btn-primary p-0"
                onClick={handleOrdersModalClose}
                style={{
                  backgroundColor: "red",
                  width: "24px",
                  height: "24px",
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto" }}>
              <CaisseOrders
                onOccupiedTableClick={handleOccupiedTableClick}
                selectedTable={selectedTable}
                handleSelectTableSession={handleSelectTableSession}
                onCloseModal={handleOrdersModalClose}
              />
            </Box>
          </Box>
        </Modal>
      </div>
      <ReloginModal
        open={lockShowModal}
        onClose={() => setLockShowModal(false)}
        openModalTables={setShowModal}
        parametrage={parametrage}
      />
    </div>
  );
};

export default Caisse;
